import React, { useState, useEffect } from 'react';
import { Button } from '../../../shared/components/ui';
import authService from '../../auth/services/authService';
import { useTranslation } from 'react-i18next';
import { buildApiUrl } from '../../../config/api';

const EditRecipeModal = ({ isOpen, onClose, recipe, onSave, isCreateMode = false, initialRecipe = null }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [steps, setSteps] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [ingredientsCatalog, setIngredientsCatalog] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        // En modo creación, usar initialRecipe; en modo edición, usar recipe
        const recipeData = isCreateMode ? initialRecipe : recipe;

        if (recipeData) {
            // Manejar nombres multiidioma (si vienen del LLM) o string simple
            const recipeName = typeof recipeData.name === 'object'
                ? (recipeData.name.es || recipeData.name.en || recipeData.name.ca || '')
                : (recipeData.name || '');

            setName(recipeName);

            // Manejar ingredientes con nombres multiidioma
            const processedIngredients = (recipeData.ingredients || []).map(ing => ({
                item: typeof ing.item === 'object'
                    ? (ing.item.es || ing.item.en || ing.item.ca || '')
                    : (ing.item || ''),
                quantity: ing.quantity || '',
                category: ing.category || ''
            }));

            setIngredients(processedIngredients);

            // Manejar steps (pueden venir como array de strings o array de objetos)
            const processedSteps = (recipeData.steps || []).map(step => {
                if (typeof step === 'string') {
                    return step;
                } else if (typeof step === 'object' && step.instruction) {
                    return step.instruction;
                } else if (typeof step === 'object' && step.content) {
                    return step.content;
                }
                return '';
            });

            setSteps(processedSteps);
        }
    }, [recipe, initialRecipe, isCreateMode]);

    // Cargar catálogo de ingredientes y categorías al abrir el modal
    useEffect(() => {
        if (isOpen) {
            loadIngredientsCatalog();
            loadCategories();
        }
    }, [isOpen]);

    const loadIngredientsCatalog = async () => {
        try {
            const response = await fetch(buildApiUrl('/api/user/ingredients'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                },
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setIngredientsCatalog(data.ingredients || []);
            }
        } catch (error) {
        }
    };

    const loadCategories = async () => {
        try {
            const response = await fetch(buildApiUrl('/api/user/ingredients/categories'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                },
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setCategories(data.categories || []);
            }
        } catch (error) {
        }
    };

    const handleAddIngredient = () => {
        setIngredients([...ingredients, { item: '', quantity: '', category: '' }]);
    };

    const handleRemoveIngredient = (index) => {
        setIngredients(ingredients.filter((_, i) => i !== index));
    };

    const handleIngredientChange = (index, field, value) => {
        const newIngredients = [...ingredients];
        newIngredients[index][field] = value;
        setIngredients(newIngredients);
    };

    const handleAddStep = () => {
        setSteps([...steps, '']);
    };

    const handleRemoveStep = (index) => {
        setSteps(steps.filter((_, i) => i !== index));
    };

    const handleStepChange = (index, value) => {
        const newSteps = [...steps];
        newSteps[index] = value;
        setSteps(newSteps);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            await onSave({
                name,
                ingredients: ingredients.filter(ing => ing.item && ing.quantity),
                steps: steps.filter(step => step.trim() !== '')
            });
            onClose();
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b p-4 sm:p-6 flex justify-between items-center gap-2 z-10">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 flex-1 min-w-0 truncate">
                        {isCreateMode ? t('menu.edit.titleNew') : t('menu.edit.titleEdit')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-3xl sm:text-2xl flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center p-2 -m-2"
                        aria-label={t('common.close')}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                    {/* Nombre de la receta */}
                    <div className="mb-4 sm:mb-6">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            {t('menu.edit.nameLabel')}
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[44px] text-sm sm:text-base"
                            required
                        />
                    </div>

                    {/* Ingredientes */}
                    <div className="mb-4 sm:mb-6">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                {t('menu.edit.ingredientsLabel')}
                            </label>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleAddIngredient}
                                className="min-h-[44px] text-sm"
                            >
                                ➕ <span className="hidden sm:inline">{t('menu.edit.addIngredient')}</span><span className="sm:hidden">{t('menu.edit.addIngredient')}</span>
                            </Button>
                        </div>

                        <div className="space-y-3">
                            {ingredients.map((ingredient, index) => (
                                <div key={index} className="bg-gray-50 p-3 sm:p-4 rounded-lg space-y-2 sm:space-y-3">
                                    {/* Nombre del ingrediente (siempre full width) */}
                                    <div className="w-full">
                                        <label className="block text-xs text-gray-600 mb-1 sm:hidden">{t('menu.edit.ingredient') || 'Ingrediente'}</label>
                                        <input
                                            type="text"
                                            placeholder={t('menu.edit.ingredientPlaceholder') || 'Nombre del ingrediente...'}
                                            value={ingredient.item}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                handleIngredientChange(index, 'item', value);
                                                // Auto-completar categoría si coincide con el catálogo
                                                const match = ingredientsCatalog.find(ing =>
                                                    ing.name.toLowerCase() === value.toLowerCase()
                                                );
                                                if (match) {
                                                    handleIngredientChange(index, 'category', match.category);
                                                }
                                            }}
                                            list={`ingredients-list-${index}`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[44px] text-sm sm:text-base"
                                            required
                                        />
                                        <datalist id={`ingredients-list-${index}`}>
                                            {ingredientsCatalog.map((ing) => (
                                                <option key={ing.id} value={ing.name} />
                                            ))}
                                        </datalist>
                                    </div>

                                    {/* Cantidad y Categoría (flex en mobile, lado a lado en desktop) */}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="flex-1">
                                            <label className="block text-xs text-gray-600 mb-1 sm:hidden">{t('menu.edit.quantity') || 'Cantidad'}</label>
                                            <input
                                                type="text"
                                                placeholder={t('menu.edit.quantityPlaceholder')}
                                                value={ingredient.quantity}
                                                onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[44px] text-sm sm:text-base"
                                                required
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs text-gray-600 mb-1 sm:hidden">{t('shoppingList.category') || 'Categoría'}</label>
                                            <select
                                                value={ingredient.category || ''}
                                                onChange={(e) => handleIngredientChange(index, 'category', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[44px] text-sm sm:text-base"
                                                required
                                            >
                                                <option value="">{t('menu.edit.selectCategory') || 'Selecciona categoría...'}</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.key} value={cat.key}>
                                                        {cat.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Botón eliminar */}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveIngredient(index)}
                                        className="w-full sm:w-auto text-red-600 hover:text-red-800 active:text-red-900 px-3 py-2 rounded-lg border border-red-200 hover:bg-red-50 transition min-h-[44px] text-sm"
                                    >
                                        🗑️ {t('menu.edit.removeIngredient')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Steps */}
                    <div className="mb-4 sm:mb-6">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700">
                                {t('menu.edit.stepsLabel')}
                            </label>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleAddStep}
                                className="min-h-[44px] text-sm"
                            >
                                ➕ <span className="hidden sm:inline">{t('menu.edit.addStep')}</span><span className="sm:hidden">{t('menu.edit.addStep')}</span>
                            </Button>
                        </div>

                        <div className="space-y-3">
                            {steps.map((step, index) => (
                                <div key={index} className="bg-gray-50 p-3 sm:p-4 rounded-lg space-y-2">
                                    <div className="flex items-start gap-2">
                                        <span className="text-xs sm:text-sm font-medium text-gray-600 mt-2 flex-shrink-0">{index + 1}.</span>
                                        <textarea
                                            placeholder={t('menu.edit.stepPlaceholder')}
                                            value={step}
                                            onChange={(e) => handleStepChange(index, e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none min-h-[44px] text-sm sm:text-base"
                                            rows="3"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveStep(index)}
                                        className="w-full sm:w-auto text-red-600 hover:text-red-800 active:text-red-900 px-3 py-2 rounded-lg border border-red-200 hover:bg-red-50 transition min-h-[44px] text-sm"
                                    >
                                        🗑️ {t('menu.edit.removeStep')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end pt-4 sm:pt-6 border-t">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            disabled={isSaving}
                            className="min-h-[48px] text-sm sm:text-base w-full sm:w-auto"
                        >
                            {t('menu.edit.cancel') || t('common.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isSaving}
                            className="min-h-[48px] text-sm sm:text-base w-full sm:w-auto"
                        >
                            {isSaving
                                ? (isCreateMode ? t('menu.edit.creating') : t('menu.edit.saving'))
                                : (isCreateMode ? t('menu.edit.save') : t('menu.edit.saveChanges'))
                            }
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditRecipeModal;
